import React, { FC } from "react";
import { Box, Chip, IconButton, Paper, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { OrderProjectValues } from "../helper";
import { CONTENT_TYPE_CONFIG } from "../../../config/contentTypeConfig";
import { formatContentTypeName } from "../helper";
import { PriceBreakdownTooltip } from "../../../components/Pricing/PriceBreakdownTooltip";
import { ProjectPricingBreakdown } from "../../../components/Pricing/PricingBreakdown";
import { buildProductDetailPath } from "../../../hooks/useProductDetailLink";
import dayjs from "dayjs";

interface ProjectSummaryRowProps {
    project: OrderProjectValues;
    fresh?: boolean;
    freshLabel?: string;
    hideActions?: boolean;
    onEdit?: () => void;
    onRemove?: () => void;
    contentTypes?: { id: number; name: string }[];
    // Estimated per-project price (from the live preview). Hidden when nullish.
    projectPrice?: number | string | null;
    // Breakdown behind the price — shown on hover. Optional.
    priceBreakdown?: ProjectPricingBreakdown | null;
    brandId?: number | string | null;
    // The order's brand — lets the price tooltip apply the RMN pricing hide
    // (draft projects carry no brand of their own).
    brand?: { rmn?: boolean } | null;
    // Optional control shown in the right-side icon cluster (e.g. a
    // selection-process chip), aligned with the price + edit/delete icons.
    rightSlot?: React.ReactNode;
    // When set, the card renders in a danger (TDC-red) state with this message
    // pinned to the bottom — e.g. "No selection for this product".
    error?: string | null;
}

function fallbackGradient(): [string, string] {
    return ["#F5E8CE", "#C49840"];
}

export const ProjectSummaryRow: FC<ProjectSummaryRowProps> = ({
    project,
    fresh,
    freshLabel = "Just added",
    hideActions,
    onEdit,
    onRemove,
    contentTypes = [],
    projectPrice,
    priceBreakdown,
    brandId,
    brand,
    rightSlot,
    error,
}) => {
    const ctName = (() => {
        if (project.content_type_id) {
            const ct = contentTypes.find((c) => c.id === project.content_type_id);
            if (ct) return ct.name;
        }
        return project.content_type || "";
    })();

    const config = CONTENT_TYPE_CONFIG[ctName] ?? null;
    const displayTitle = config?.title ?? (ctName ? formatContentTypeName(ctName) : "—");
    const [c1, c2] = fallbackGradient();

    const channelDisplay = project.channel_name || project.channel_objects?.map((c) => c.name).join(", ") || project.channel || "—";
    const productDisplay = project.product_name || "—";
    const startDisplay = project.start_date ? dayjs(project.start_date).format("MMM D, YYYY") : "—";

    const productIds = project.product_ids?.length
        ? project.product_ids
        : project.product_id != null
        ? [project.product_id]
        : [];
    const productPath =
        brandId != null && productIds.length === 1
            ? buildProductDetailPath({ desireid: productIds[0], brand_id: brandId })
            : null;

    // Prefer an explicit product image, then the matching product's image from
    // the attached products list, then any product image — so the thumbnail
    // shows a real image whenever one is available.
    const thumbnailSrc =
        project.product_image ||
        project.products?.find((pr) => productIds.includes(pr.desireid))?.image ||
        project.products?.[0]?.image ||
        null;

    const hasError = !!error;

    return (
        <Paper
            elevation={0}
            sx={{
                // The error WASH is tokenized (--down-bg) because every child of
                // this card is tokenized — a literal light wash left the text
                // unreadable in dark. The error BORDER stays the literal TDC red:
                // --down is a maroon chip text color, not this alert red.
                background: hasError ? "var(--down-bg)" : fresh ? "var(--tint)" : "var(--surface)",
                border: `${hasError || fresh ? 1.5 : 1}px solid ${
                    hasError ? "#EB3E33" : fresh ? "var(--accent)" : "var(--border)"
                }`,
                borderRadius: "12px",
                padding: "12px 14px",
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                "@keyframes slideIn": {
                    from: { opacity: 0, transform: "translateY(6px)" },
                    to: { opacity: 1, transform: "translateY(0)" },
                },
                ...(fresh && { animation: "slideIn .25s ease" }),
            }}
        >
            {/* Primary row */}
            <Box display="flex" alignItems="center" gap="12px">
            {/* Thumbnail */}
            <Box
                sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "1rem",
                    overflow: "hidden",
                    flexShrink: 0,
                    // Uploaded product art usually has white baked in — a white
                    // plate makes it read as intentional rather than as a hole.
                    background: thumbnailSrc ? "#FFFFFF" : `linear-gradient(135deg, ${c1} 0%, ${c2} 100%)`,
                }}
            >
                {thumbnailSrc && (
                    <img 
                        src={thumbnailSrc}
                        alt={productDisplay}
                        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    />
                )}
            </Box>

            {/* Content */}
            <Box flex={1} minWidth={0}>
                <Box display="flex" alignItems="center" gap="8px" mb="2px">
                    <Typography
                        sx={{
                            fontSize: 13.5,
                            fontWeight: 600,
                            color: "var(--text)",
                            fontFamily: "CircularStd",
                        }}
                    >
                        {displayTitle}
                    </Typography>
                    {fresh && (
                        <Chip
                            label={freshLabel}
                            size="small"
                            variant="outlined"
                            sx={{
                                fontSize: 9.5,
                                fontWeight: 700,
                                color: "var(--accent-text)",
                                borderColor: "var(--accent)",
                                height: 20,
                                fontFamily: "CircularStd",
                                "& .MuiChip-label": { px: "6px" },
                            }}
                        />
                    )}
                </Box>
                <Box
                    display="flex"
                    alignItems="center"
                    flexWrap="wrap"
                    gap="6px"
                    sx={{ fontSize: 11.5, color: "var(--text-2)", fontFamily: "CircularStd" }}
                >
                    <span>{channelDisplay}</span>
                    <Box sx={{ width: 3, height: 3, borderRadius: "50%", background: "var(--muted)", flexShrink: 0 }} />
                    <Box
                        component={productPath ? "a" : "span"}
                        href={productPath || undefined}
                        target={productPath ? "_blank" : undefined}
                        rel={productPath ? "noopener noreferrer" : undefined}
                        sx={{
                            fontWeight: 600,
                            color: "var(--text)",
                            ...(productPath && {
                                cursor: "pointer",
                                textDecoration: "none",
                                "&:hover": { textDecoration: "underline" },
                            }),
                        }}
                    >
                        {productDisplay}
                    </Box>
                    <Box sx={{ width: 3, height: 3, borderRadius: "50%", background: "var(--muted)", flexShrink: 0 }} />
                    <Box display="flex" alignItems="center" gap="3px">
                        <CalendarTodayIcon sx={{ fontSize: 11 }} />
                        <span>{startDisplay}</span>
                    </Box>
                </Box>
            </Box>

            {/* Selection-process chip — sits in the right-side icon cluster,
                vertically aligned with the price + edit/delete icons. */}
            {rightSlot && <Box sx={{ flexShrink: 0, display: "flex" }}>{rightSlot}</Box>}

            {/* Estimated price — icon only, hover for the price + breakdown */}
            {projectPrice != null && (
                <PriceBreakdownTooltip
                    breakdown={priceBreakdown}
                    pricingTotal={projectPrice}
                    project={brand ? { ...project, brand } : project}
                />
            )}

            {/* Actions */}
            {!hideActions && (
                <Box display="flex" gap="2px">
                    {onEdit && (
                        <IconButton size="small" onClick={onEdit} sx={{ color: "var(--text-2)", borderRadius: "6px" }}>
                            <EditIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    )}
                    {onRemove && (
                        <IconButton size="small" onClick={onRemove} sx={{ color: "var(--text-2)", borderRadius: "6px" }}>
                            <DeleteIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                    )}
                </Box>
            )}
            </Box>

            {/* Error strip — pinned to the bottom of the card */}
            {hasError && (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        pt: "8px",
                        borderTop: "1px solid #F6C9C5",
                    }}
                >
                    <ErrorOutlineRoundedIcon sx={{ fontSize: 14, color: "#EB3E33", flexShrink: 0 }} />
                    <Typography
                        sx={{
                            fontSize: 11.5,
                            fontWeight: 600,
                            color: "#C0392B",
                            fontFamily: "CircularStd",
                        }}
                    >
                        {error}
                    </Typography>
                </Box>
            )}
        </Paper>
    );
};
